import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import {
	Checkbox,
	Divider,
	Dropzone,
	ErrorText,
	Heading,
	Loader,
	Option,
	Radio,
	Select,
	TextArea,
	TextBlock,
	TextField
} from 'neon-design-system';
import { Box, createStyles, Grid, makeStyles, Paper } from '@material-ui/core';
import { useConstrainTextArea, useFormControl, useFormGroup, useManageTemplateForm } from '../../../utils/form.utils';
import {
	maxLengthValidator,
	maxNumberValidator,
	minNumberOrEmptyValidator,
	priceValidator,
	requiredValidator
} from '../../../utils/validator.utils';
import ManageTemplate, { ManageTemplateInstance } from '../../ui/ManageTemplate';
import ManageTemplateFormItem from '../../ui/ManageTemplateFormItem';
import { baseUrls } from '../../../utils/baseUrls.utils';
import {
	ADD_PREMIUM_CHANNEL,
	ARCHIVE_PREMIUM_CHANNEL,
	EDIT_PREMIUM_CHANNEL,
	GET_PREMIUM_CHANNEL_BY_ID_PUBLISHERS_DASHBOARD
} from '../../../gql/channels';
import { DESKS_SHORT } from '../../../gql/desks';
import { Channel, ChannelFreeType, ChannelOwner, ChannelState, ChannelType, ChannelTypes, Desk, User } from '../../../shared/types';
import { config } from '../../../config';
import { useAuth0Data } from 'neon-auth0';
import { sortArray } from '../../../utils/sorting.utils';
import { getContributorRoleUsers, getUsersWithNames } from '../../../utils/filtering.utils';
import { useUser } from '../../providers/UserProvider';
import { PAGINATED_USERS_SEARCH, USERS } from '../../../gql/users';
import jwt from 'jwt-decode';
import { isChannelAdmin, isUserNotChannelAdmin } from '../../../utils/channel.utils';
import { getFileName } from '../../../utils/utils';
import { ResearchRole } from '../../../shared/global-enums';
import { debounce } from 'lodash';
import { capitalizeFirstLetter } from '../../../utils/string.utils';

const useStyles = makeStyles(theme => createStyles({
	container: {
		display: 'flex',
		justifyContent: 'center',
		width: '100%',
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		paddingBottom: theme.spacing(6)
	},
	main: {
		flex: 1,
		flexDirection: 'column',
		maxWidth: 1200
	},
	labelRadioButton: {
		fontWeight: 'bold',
		marginBottom: theme.spacing(1)
	},
	subscriptionType: {
		padding: 16
	}
}));

interface FixedOption extends Option {
	isFixed: boolean;
}

export const PremiumChannelsAddEdit = (): JSX.Element => {
	const { _id } = useParams<{ _id: string }>();
	const history = useHistory();
	const classes = useStyles();
	const me = useUser();
	const isAdmin = [ResearchRole.Administrator].some(me.hasScope);

	const { accessToken } = useAuth0Data();
	const neonUserId = jwt<any>(accessToken)[config.auth0.namespace + 'neonUserId'];

	const [channelOwnerType, setChannelOwnerType] = useState<ChannelOwner | null>(null);
	const [desksOptions, setDesksOptions] = useState<Option[]>([]);
	const [articleCount, setArticleCount] = useState<number>(0);
	const [templateCount, setTemplateCount] = useState<number>(0);
	const [activeUserCount, setActiveUserCount] = useState<number>(0);
	const [contributorsOptions, setContributorsOptions] = useState<Option[]>([]);

	const glAccountValidators = [requiredValidator, (val: string) => maxLengthValidator(val?.length, 32)];
	const priceValidators = [requiredValidator, priceValidator];
	const trialDaysValidators = [
		(val: number) => minNumberOrEmptyValidator(val, config.minTrialDays),
		(val: number) => maxNumberValidator(val, config.maxTrialDays)
	];

	const nameFormControl = useFormControl<string>('', [requiredValidator]);
	const channelTypeControl = useFormControl<ChannelType | null>(null, [requiredValidator]);
	const freeChannelTypeControl = useFormControl<ChannelFreeType | null>(null, []);
	const channelAdminsFormControl = useFormControl<FixedOption[] | undefined>([], [requiredValidator]);
	const channelOwnerFormControl = useFormControl<Option | undefined>(undefined, [requiredValidator]);
	const priceFormControl = useFormControl<string>('', priceValidators);
	const descriptionFormControl = useFormControl<string>('', [requiredValidator]);
	const constrainTextArea = useConstrainTextArea(0, 250);
	const urlFormControl = useFormControl<string>('', []);
	const profilePictureControl = useFormControl<string>('', []);
	const coverImageControl = useFormControl<string>('', []);
	const glAccountFormControl = useFormControl<string>('', glAccountValidators);
	const allUsers = useFormControl<boolean>(false, []);
	const hideDisclaimerControl = useFormControl<boolean>(false, []);

	const isPremiumChannelSubscriptionType = (channelType: ChannelType | null) => channelType === ChannelType.premium;
	const defaultTrialDurationDaysControl = useFormControl<string>('', trialDaysValidators);

	const { refetch: fetchUsers } = useQuery(PAGINATED_USERS_SEARCH, {
		skip: true
	});

	const [getChannel, { loading, data }] = useLazyQuery(GET_PREMIUM_CHANNEL_BY_ID_PUBLISHERS_DASHBOARD({ includePreviouslySubscribedUsers: true }), {
		variables: { id: _id },
		fetchPolicy: 'network-only',
		onCompleted: ({ channel }) => {
			if (channel) {
				nameFormControl?.setInitialValue(channel.name);
				setChannelOwnerType(channel.ownerType);
				priceFormControl?.setInitialValue(channel.price);
				descriptionFormControl?.setInitialValue(channel.description);
				urlFormControl?.setInitialValue(channel.url);
				profilePictureControl?.setInitialValue(channel.profileImage);
				coverImageControl?.setInitialValue(channel.coverImage);
				glAccountFormControl?.setInitialValue(channel.glAccount);
				defaultTrialDurationDaysControl?.setInitialValue(channel.defaultTrialDurationDays);
				channelTypeControl?.setInitialValue(!isPremiumChannelSubscriptionType(channel.type) ? ChannelType.free : ChannelType.premium);
				hideDisclaimerControl?.setInitialValue(channel.hideDisclaimer);
				setArticleCount(channel.articleCount);
				setTemplateCount(channel.templateCount);
				setActiveUserCount(channel.channelActiveUsersCount);

				if (!isPremiumChannelSubscriptionType(channel.type)) {
					if (channel.type === ChannelTypes.freeOpen) {
						freeChannelTypeControl.setInitialValue(ChannelFreeType.open);
					} else {
						freeChannelTypeControl.setInitialValue(ChannelFreeType.closed);
					}
				}

				channelAdminsFormControl?.setInitialValue(channel.adminUsers?.map((user: User): FixedOption => ({
					label: user.name,
					value: user._id,
					isFixed: me?.userDetails?.id === user._id
				})) ?? []);
			}
		}
	});

	const { loading: desksLoading, data: desksData } = useQuery(DESKS_SHORT, {
		fetchPolicy: 'network-only',
		onCompleted: ({ documents }) => {
			if (documents) {
				const desksOptions: Option[] = documents.map((desk: Desk) => ({
					label: desk.name,
					value: desk._id
				}));
				setDesksOptions(desksOptions);
			}
		}
	});

	const { loading: contributorsLoading, data: contributorsData } = useQuery(USERS, {
		fetchPolicy: 'network-only',
		onCompleted: ({ documents }) => {
			if (documents) {
				const contributorsOptions: Option[] = sortArray(getContributorRoleUsers(getUsersWithNames(documents)).map((user: User) => ({
					label: `${user.name} (${user.email})`,
					value: user._id
				})), 'label');
				setContributorsOptions(contributorsOptions);
			}
		}
	});

	useEffect(() => {
		if (_id) {
			getChannel();
			return;
		}

		if (me?.userDetails) {
			channelAdminsFormControl?.setInitialValue([{
				label: me?.userDetails?.name,
				value: me?.userDetails?.id,
				isFixed: true
			}]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [_id, getChannel]);

	useEffect(() => {
		if (data && desksData) {
			if (data.channel.ownerType !== 'desk') return;

			const currentChannelOwner = desksData.documents.find((item: Desk) => item._id === data.channel.ownerId);

			if (currentChannelOwner) {
				channelOwnerFormControl?.setInitialValue({
					label: currentChannelOwner.name,
					value: currentChannelOwner._id
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, desksData]);

	useEffect(() => {
		if (data && contributorsData) {
			if (data.channel.ownerType !== 'contributor') return;

			const currentChannelOwner = contributorsData.documents.find((item: User) => item._id === data.channel.ownerId);

			if (currentChannelOwner) {
				channelOwnerFormControl?.setInitialValue({
					label: `${currentChannelOwner.name} (${currentChannelOwner.email})`,
					value: currentChannelOwner._id
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, contributorsData]);

	const editItem: Channel = data?.channel;

	useEffect(() => {
		if (isAdmin) return;
		if (data && _id && isUserNotChannelAdmin(data.channel.adminUsers, neonUserId)) {
			history.push(baseUrls.publisher.premiumChannels.list)
		}
	}, [data, _id])

	useEffect(() => {
		if (isPremiumChannelSubscriptionType(channelTypeControl.value)) {
			priceFormControl.setValidators(priceValidators);
			glAccountFormControl.setValidators(glAccountValidators);
			freeChannelTypeControl.setValidators([]);
			defaultTrialDurationDaysControl.setValidators(trialDaysValidators);
		} else {
			priceFormControl.setValidators([]);
			glAccountFormControl.setValidators([]);
			freeChannelTypeControl.setValidators([requiredValidator]);
			defaultTrialDurationDaysControl.setValidators([]);
		}
	}, [channelTypeControl.value])

	const form = useFormGroup({
		name: nameFormControl,
		adminUsers: channelAdminsFormControl,
		ownerId: channelOwnerFormControl,
		price: priceFormControl,
		glAccount: glAccountFormControl,
		description: descriptionFormControl,
		url: urlFormControl,
		profileImage: profilePictureControl,
		coverImage: coverImageControl,
		defaultTrialDurationDays: defaultTrialDurationDaysControl,
		channelType: channelTypeControl,
		freeChannelType: freeChannelTypeControl,
		hideDisclaimer: hideDisclaimerControl,
		allUsers
	});

	const returnListUrl = baseUrls.publisher.premiumChannels.list;

	const { isNew, formMessage, formBusy, onSubmit, onCancel, onDelete, onAlertBoxClose } = useManageTemplateForm(
		ManageTemplateInstance.Channel,
		form,
		editItem,
		ADD_PREMIUM_CHANNEL,
		EDIT_PREMIUM_CHANNEL,
		ARCHIVE_PREMIUM_CHANNEL,
		returnListUrl
	);

	const findUsers = useCallback(debounce((input: string, callback) => {
		fetchUsers({
			params: {
				searchQuery: input
			}
		}).then(r => {
			const users: User[] = r.data.getPaginatedUsers.users;
			const filteredUsers = form.allUsers.value ? users : getContributorRoleUsers(users);

			const usersSelectOptions: FixedOption[] = sortArray(
				filteredUsers.map((user): FixedOption => ({
					label: `${user.name} (${user.email})`,
					value: user._id,
					isFixed: me?.userDetails?.id === user._id
				})), 'label');
			callback(usersSelectOptions);
		});
	}, 500), [form.allUsers]);

	const getReqBody = () => {
		const { value } = form;
		const trialDurationValue = parseInt(defaultTrialDurationDaysControl.value);

		const type = isPremiumChannelSubscriptionType(value.channelType) ? ChannelTypes.premium :
			freeChannelTypeControl.value === ChannelFreeType.open ? ChannelTypes.freeOpen : ChannelTypes.freeClosed;

		return {
			...value,
			defaultTrialDurationDays: isPremiumChannelSubscriptionType(value.channelType) ? trialDurationValue
				? trialDurationValue
				: config.defaultTrialChannelSubscriptionDays : undefined,
			price: value.price ? +value.price : undefined,
			glAccount: isPremiumChannelSubscriptionType(value.channelType) ? value.glAccount : undefined,
			ownerType: channelOwnerType,
			adminUsers: value.adminUsers.map((u: Option) => u.value),
			type,
			hideDisclaimer: value.hideDisclaimer,
			channelType: undefined,
			freeChannelType: undefined,
			allUsers: undefined
		}
	}

	if (loading ||
		desksLoading ||
		contributorsLoading ||
		(!isAdmin && (data?.channel && isUserNotChannelAdmin(data.channel.adminUsers, neonUserId)))
	) return <Loader />;

	const selectedChannelAdmins = channelAdminsFormControl?.value ?? [];
	const selectedChannelOwner = channelOwnerFormControl.value ?? null;
	const isReadOnly = data?.channel?.state === ChannelState.Archived
	const disableDialogDelete = data?.channel.channelActiveUsersCount > 0 || data?.channel.state === ChannelState.Archived;
	const showArchive: boolean = isAdmin || (data?.channel && isChannelAdmin(data.channel.adminUsers, neonUserId));

	return (
		<div className={classes.container}>
			<div className={classes.main}>
				<ManageTemplate
					dialogMessage={
						disableDialogDelete
							? <Grid container spacing={2}>
								<Grid item xs={12}>
									You cannot archive this channel because it has active subscribers.
								</Grid>
								<Grid item xs={12}>
									<Grid container spacing={0}>
										<Grid item xs={12}>
											{activeUserCount} active subscribers
										</Grid>
									</Grid>
								</Grid>
							</Grid>
							: <Grid container spacing={2}>
								<Grid item xs={12}>
									You are about to archive channel <strong>{nameFormControl?.value}</strong>.
									This action cannot be undone.
								</Grid>
								<Grid item xs={12}>
									<Grid container spacing={0}>
										<Grid item xs={12}>
											As part of this process:
										</Grid>
										<Grid item xs={12}>
											{articleCount} articles will be archived
										</Grid>
										<Grid item xs={12} >
											{templateCount} templates will be unlinked
										</Grid>
									</Grid>
								</Grid>
							</Grid>
					}
					showDelete={showArchive}
					deleteDialogBtnDisabled={disableDialogDelete}
					onDelete={(): void => {
						onDelete(nameFormControl?.value);
					}}
					pathArea={'Channels'}
					returnUrl={returnListUrl}
					templateInstance={ManageTemplateInstance.Channel}
					isNew={isNew}
					formMessage={formMessage}
					formBusy={formBusy}
					onCancel={onCancel}
					onSubmit={(ev: any): void => {
						onSubmit(ev, getReqBody());
					}}
					onAlertBoxClose={onAlertBoxClose}
					templateCreateButtonLabel="Save channel"
					templateUpdateButtonLabel="Save changes"
					templateDeleteButtonLabel="Archive channel"
					templateDeleteDialogTitle="Archive"
					showCancelButton={false}
					isReadOnly={isReadOnly}
					titleOverride={isNew ? 'Create a new Channel' : `Edit ${data?.channel.name}`}
					outOfFormContent={
						<>
							{
								isReadOnly && profilePictureControl?.value?.length === 0
									? null
									: <ManageTemplateFormItem>
										<TextBlock styleName="caption1" className={classes.labelRadioButton}>Profile picture</TextBlock>
										<Dropzone
											isSuccess={!!profilePictureControl.value}
											url={config.serverUrl + '/upload'}
											config={{
												headers: { Authorization: `Bearer ${accessToken}` },
												init() {
													this.on('success', (file: File, response: any) => {
														profilePictureControl.setValue(response.link);
													});

													this.on('removedfile', () => {
														profilePictureControl.setValue('');
													});

													if (profilePictureControl.value) {
														this.displayExistingFile({
															name: getFileName(profilePictureControl.value),
															status: 'Ready',
															dataUrl: profilePictureControl.value
														});
													}
												}
											}}
											description="Allowed file formats are PNG or JPG. Maximum file size 2MB."
											acceptedFiles=".png, .jpg, .jpeg"
											maxFilesize={2}
										/>
									</ManageTemplateFormItem>
							}
							{
								isReadOnly && coverImageControl?.value?.length === 0
									? null
									: <ManageTemplateFormItem>
										<TextBlock styleName="caption1" className={classes.labelRadioButton}>Cover image</TextBlock>
										<Dropzone
											isSuccess={!!coverImageControl.value}
											url={config.serverUrl + '/upload'}
											config={{
												headers: { Authorization: `Bearer ${accessToken}` },
												init() {
													this.on('success', (file: File, response: any) => {
														coverImageControl.setValue(response.link);
													});

													this.on('removedfile', () => {
														coverImageControl.setValue('');
													});

													if (coverImageControl.value) {
														this.displayExistingFile({
															name: getFileName(coverImageControl.value),
															status: 'Ready',
															dataUrl: coverImageControl.value
														});
													}
												}
											}}
											description="Allowed file formats are PNG or JPG. Maximum file size 2MB."
											acceptedFiles=".png, .jpg, .jpeg"
											maxFilesize={2}
										/>
									</ManageTemplateFormItem>
							}
						</>
					}
				>
					<ManageTemplateFormItem>
						<Paper className={classes.subscriptionType}>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<Heading styleName="headline3">Subscription type</Heading>
								</Grid>
								<Grid item xs={12}>
									<TextBlock styleName="footnote" ink="muted">
										With a <b>channel</b> you can put your articles behind a paywall.
										Users will have to subscribe and pay a monthly subscription to access it.
									</TextBlock>
								</Grid>
								<Grid item xs={12}>
									<Radio
										checked={channelTypeControl.value === ChannelType.premium}
										onChange={() => channelTypeControl.setValue(ChannelType.premium)}
										value="v"
										label={capitalizeFirstLetter(ChannelType.premium)}
										disabled={!!editItem || isReadOnly}
									/>
									<Radio
										checked={channelTypeControl.value === ChannelType.free}
										onChange={() => channelTypeControl.setValue(ChannelType.free)}
										value="v"
										label={capitalizeFirstLetter(ChannelType.free)}
										disabled={!!editItem || isReadOnly}
									/>
									{channelTypeControl.showError && (
										<Box mt={1}>
											<ErrorText>{channelTypeControl.validationMessage}</ErrorText>
										</Box>
									)}
								</Grid>

								{
									channelTypeControl.value && (
										<>
											<Grid item xs={12}><Divider /></Grid>
											{
												isPremiumChannelSubscriptionType(channelTypeControl.value) ? (
													<>
														<Grid item xs={12}>
															<TextField
																disabled={!!editItem || isReadOnly}
																id={'price-per-month-input'}
																fullWidth
																prefix="$"
																helperText="Admin won’t be able to change this field without contacting support."
																error={priceFormControl?.showError}
																errorText={priceFormControl?.validationMessage}
																pattern="^[0-9]+(\.[0-9]{1,2})?$"
																inputMode="numeric"
																label="Price per month"
																autoComplete="off"
																value={priceFormControl?.value}
																onKeyPress={e => (e?.key === '-' || e?.key === '+') && e.preventDefault()}
																onBlur={e => {
																	if (priceFormControl?.value) {
																		priceFormControl?.setValue(parseFloat(priceFormControl?.value).toFixed(2));
																	}
																	priceFormControl?.handleBlur(e);
																}}
																onChange={(e: ChangeEvent<any>): void => {
																	const val: string = e?.target?.value;
																	if (val?.length > 100) {
																		return;
																	}
																	priceFormControl?.setValue(val);
																}}
																tooltip={editItem ? {
																	title: 'You must contact support to change the price of a channel.'
																} : undefined}
															/>
														</Grid>
														<Grid item xs={12}>
															<TextField
																disabled={isReadOnly}
																required={false}
																placeholder={config.defaultTrialChannelSubscriptionDays.toString()}
																postfix={'days'}
																helperText="You can specify a bespoke trial duration for this channel."
																id={'trial-duration-days-input'}
																fullWidth
																error={defaultTrialDurationDaysControl?.showError}
																errorText={defaultTrialDurationDaysControl?.validationMessage}
																inputMode="numeric"
																label="Default Trial Duration"
																autoComplete="off"
																value={defaultTrialDurationDaysControl?.value}
																onBlur={defaultTrialDurationDaysControl?.handleBlur}
																onChange={(e: ChangeEvent<any>): void => {
																	const val: string = e?.target?.value;

																	if (val?.length > 7) return;

																	const parsedVal = parseInt(val);

																	if (parsedVal || parsedVal === 0) {
																		defaultTrialDurationDaysControl?.setValue(parsedVal);
																		return;
																	}

																	if (val === '') defaultTrialDurationDaysControl?.setValue(val);
																}}
															/>
														</Grid>
														<Grid item xs={12}>
															<TextField
																id={'premium-channels-gl-account-input'}
																disabled={isReadOnly}
																fullWidth
																helperText="If you don't know your GL account, please contact Product Control in Finance."
																error={glAccountFormControl?.showError}
																errorText={glAccountFormControl?.validationMessage}
																type="text"
																label="Peoplesoft GL account"
																autoComplete="off"
																value={glAccountFormControl?.value}
																onBlur={glAccountFormControl?.handleBlur}
																onChange={(e: ChangeEvent<any>): void => {
																	const val: string = e?.target?.value;
																	if (val?.length > 32) {
																		return;
																	}
																	glAccountFormControl?.setValue(val);
																}}
															/>
														</Grid>
													</>
												) : (
													<>
														<Grid item xs={12}>
															<TextBlock styleName="caption1"><b>Content availability in your channel</b></TextBlock>
															<Box mt={1}>
																<Radio
																	disabled={isReadOnly}
																	checked={freeChannelTypeControl.value === ChannelFreeType.open}
																	onChange={() => freeChannelTypeControl.setValue(ChannelFreeType.open)}
																	value="v"
																	label={capitalizeFirstLetter(ChannelFreeType.open)}
																/>
																<Radio
																	disabled={isReadOnly}
																	checked={freeChannelTypeControl.value === ChannelFreeType.closed}
																	onChange={() => freeChannelTypeControl.setValue(ChannelFreeType.closed)}
																	value="v"
																	label={capitalizeFirstLetter(ChannelFreeType.closed)}
																/>
															</Box>
															{freeChannelTypeControl.showError && (
																<Box mt={1}>
																	<ErrorText>{freeChannelTypeControl.validationMessage}</ErrorText>
																</Box>
															)}
														</Grid>
														<Grid item xs={12}>
															<TextBlock styleName="footnote" ink="muted">
																<b>Open:</b> content published under an ‘open’ free channel is available to everyone on Insights.
															</TextBlock>
														</Grid>
														<Grid item xs={12}>
															<TextBlock styleName="footnote" ink="muted">
																<b>Closed:</b> Insights users will have to request access to your channel.
																You can control who has access by confirming or rejecting subscription requests.
															</TextBlock>
														</Grid>
													</>
												)
											}
										</>
									)
								}
							</Grid>
						</Paper>
					</ManageTemplateFormItem>
					<ManageTemplateFormItem>
						<TextBlock styleName="caption1" color="grey" shade={200} className={classes.labelRadioButton}>
							Owner
						</TextBlock>
						<Radio
							disabled={isReadOnly}
							checked={channelOwnerType === ChannelOwner.desk}
							onChange={() => {
								setChannelOwnerType(ChannelOwner.desk)
								channelOwnerFormControl.setValue(null);
							}}
							value="v"
							label="Desk"
						/>
						<Radio
							disabled={isReadOnly}
							checked={channelOwnerType === ChannelOwner.contributor}
							onChange={() => {
								setChannelOwnerType(ChannelOwner.contributor)
								channelOwnerFormControl.setValue(null);
							}}
							value="v"
							label="Contributor"
						/>
						{channelOwnerFormControl.showError && !channelOwnerType && (
							<Box mt={1}>
								<ErrorText>{channelOwnerFormControl.validationMessage}</ErrorText>
							</Box>
						)}
						{
							channelOwnerType && (
								<Box mt={1}>
									{
										allUsers.value ? (
											<Select
												id={'channel-owner-select'}
												disabled={isReadOnly}
												size="medium"
												loadOptions={findUsers}
												menuPlacement="bottom"
												placeholder="Start typing…"
												value={selectedChannelOwner}
												onBlur={channelOwnerFormControl?.handleBlur}
												error={channelOwnerFormControl?.showError}
												errorText={channelOwnerFormControl?.validationMessage}
												onChange={(opt: Option): void => {
													channelOwnerFormControl?.setValue(opt);
												}}
												clearable={false}
												helperText="The owner is public facing and is shown to subscribers."
											/>
										) : (
											<Select
												id={'channel-owner-select'}
												disabled={isReadOnly}
												size="medium"
												menuPlacement="bottom"
												placeholder="Select from the list..."
												value={channelOwnerFormControl?.value}
												onBlur={channelOwnerFormControl?.handleBlur}
												error={channelOwnerFormControl?.showError}
												errorText={channelOwnerFormControl?.validationMessage}
												onChange={(opt: Option): void => {
													channelOwnerFormControl?.setValue(opt);
												}}
												options={channelOwnerType === ChannelOwner.contributor ? contributorsOptions : desksOptions}
												clearable={false}
												helperText="The owner is public facing and is shown to subscribers."
											/>
										)
									}
								</Box>
							)
						}
						<Box mt={1}>
							{
								channelOwnerType === ChannelOwner.contributor && (
									<Checkbox
										disabled={isReadOnly}
										id="desk-all-users-checkbox"
										label="Show all users"
										checked={form.allUsers?.value}
										value="a"
										onChange={(e: ChangeEvent<any>): void => {
											form.allUsers.setValue(e?.target?.checked);
										}}
									/>
								)
							}
						</Box>
					</ManageTemplateFormItem>
					<ManageTemplateFormItem>
						<Select
							id={'channel-admins-select'}
							disabled={isReadOnly}
							size="medium"
							clearable={true}
							multi={true}
							loadOptions={findUsers}
							menuPlacement="bottom"
							placeholder="Select from the list…"
							label="Admins"
							value={
								selectedChannelAdmins
									.filter(option => option.isFixed)
									.concat(selectedChannelAdmins
										.filter(option => !option.isFixed)
										.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())))
							}
							error={channelAdminsFormControl.showError}
							errorText={channelAdminsFormControl.validationMessage}
							onBlur={channelAdminsFormControl.handleBlur}
							onChange={(options: FixedOption[] = [], action: { removedValue: FixedOption }): void => {
								if (action?.removedValue?.isFixed) return;
								channelAdminsFormControl?.setValue(options);
							}}
							helperText="Channel admins can edit Channel details and manage subscribers."
						/>
					</ManageTemplateFormItem>
					<ManageTemplateFormItem>
						<TextField
							id={'premium-channel-name-input'}
							disabled={isReadOnly}
							fullWidth
							error={nameFormControl?.showError}
							errorText={nameFormControl?.validationMessage}
							type="text"
							label="Channel name"
							autoComplete="off"
							value={nameFormControl?.value}
							onBlur={nameFormControl?.handleBlur}
							onChange={(e: ChangeEvent<any>): void => {
								const val: string = e?.target?.value;
								if (val?.length > 100) {
									return;
								}
								nameFormControl?.setValue(val);
							}}
						/>
					</ManageTemplateFormItem>
					<ManageTemplateFormItem>
						<TextArea
							id={'channel-description-textarea'}
							disabled={isReadOnly}
							fullWidth
							type="text"
							label="Short description"
							error={descriptionFormControl?.showError}
							errorText={descriptionFormControl?.validationMessage}
							progressBarHelperText={constrainTextArea?.charsMessage}
							autoComplete="off"
							rowsMax={4}
							value={descriptionFormControl?.value}
							showProgressBar={true}
							progressBarIndicator={constrainTextArea?.currentChars}
							progressBarLimit={constrainTextArea?.charsLimit}
							onBlur={descriptionFormControl?.handleBlur}
							onChange={(e: ChangeEvent<any>): void => {
								const val: string = e?.target?.value;
								if (val?.length > constrainTextArea?.charsLimit) {
									return;
								}
								constrainTextArea?.setCurrentChars(val?.length)
								descriptionFormControl?.setValue(val);
							}}
						/>
					</ManageTemplateFormItem>
					<ManageTemplateFormItem>
						<TextField
							id={'premium-channels-url-input'}
							disabled={isReadOnly}
							fullWidth
							helperText="A link to neon.markets which tells users more about the Channel and its contributors."
							error={urlFormControl?.showError}
							errorText={urlFormControl?.validationMessage}
							type="text"
							label="Marketing webpage URL"
							autoComplete="off"
							value={urlFormControl?.value}
							onBlur={urlFormControl?.handleBlur}
							onChange={(e: ChangeEvent<any>): void => {
								const val: string = e?.target?.value;
								if (val?.length > 100) {
									return;
								}
								urlFormControl?.setValue(val);
							}}
						/>
					</ManageTemplateFormItem>
					<ManageTemplateFormItem>
						<Checkbox
							id="hide-disclainer-checkbox"
							label="Hide disclaimers for all articles in this channel. Publishers are now liable for embedding disclaimers in all articles."
							value="hideDisclaimer"
							checked={hideDisclaimerControl?.value}
							onChange={({ target }) => {
								hideDisclaimerControl?.setValue(target.checked)
							}}
						/>
					</ManageTemplateFormItem>
				</ManageTemplate>
			</div>
		</div>
	);
};

export default PremiumChannelsAddEdit;
